import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { createApi } from '@reduxjs/toolkit/query/react';
import { formDepositMethodInfo, formDepositRequiredFields } from 'store/formatters';
import { createBaseQueryWithPwPrefix } from 'store/middleware/pwContextMiddleware';
import type {
  PayHubCashierContractsPaymentsSelectPaymentGroupResponse as DepositMethodInfoResponse,
  PayHubCashierContractsPaymentsSubmitPaymentGroupRequest,
  PayHubCashierContractsPayoutsInitPayoutGroupsResponse as InitSuccess,
  PayHubWidgetContractsSubmitPaymentResponse,
  PayHubWidgetContractsSubmitPayoutResponse,
} from 'store/models';
import { WIDGET_API } from 'utils/constants';

interface UploadDocFileRequest {
  payGroupId: number;
  requestBody: FormData;
  signal?: AbortSignal;
}

type UploadDocFileResponse = void;

export interface CustomServerError {
  status: Pick<FetchBaseQueryError, 'status'> | string | number;
  errorMessage?: string;
  errorCode?: number;
  error_code?: number;
}

type ExtendedFetchBaseQueryError = FetchBaseQueryError & {
  data?: {
    error_message: string;
    error_code: number;
  };
};

export interface DepositSubmitRequestProps {
  payGroupId: number;
  requestBody: PayHubCashierContractsPaymentsSubmitPaymentGroupRequest;
  signal: AbortSignal;
}

export type GetDepositMethodInfoRequestProps = {
  payGroupId: number;
  currency: string;
};

export type GetDepositMethodsRequestProps =
  | {
      payGroupId?: number;
      skipRedirectMethod?: boolean;
      isOnRamp?: boolean;
      hasOnRampCurrency?: boolean;
    }
  | undefined;

export const INIT_TAG = 'init_deposit';
export const SELECT_TAG = 'select_deposit';
export const SUBMIT_TAG = 'submit_deposit';

export const depositAPI = createApi({
  reducerPath: 'depositAPI',
  tagTypes: [INIT_TAG, SELECT_TAG, SUBMIT_TAG],
  baseQuery: createBaseQueryWithPwPrefix(WIDGET_API),
  endpoints(builder) {
    return {
      getDepositMethods: builder.query<InitSuccess, string | void>({
        query: (currencyName) => ({
          url: currencyName ? `/payments/groups/init?currency=${currencyName}` : '/payments/groups/init',
        }),
        keepUnusedDataFor: Infinity,
        providesTags: [INIT_TAG],
      }),
    };
  },
});

export const extendedDepositAPI = depositAPI.injectEndpoints({
  endpoints: (builder) => ({
    getDepositMethodInfo: builder.query<DepositMethodInfoResponse, GetDepositMethodInfoRequestProps>({
      query: ({ payGroupId, currency }) => ({
        url: `/payments/groups/${payGroupId}/select`,
        method: 'POST',
        body: {
          currency,
        },
        keepalive: true,
      }),
      keepUnusedDataFor: Infinity,
      providesTags: (_result, _error, { payGroupId }) => [
        {
          type: SELECT_TAG,
          id: payGroupId,
        },
      ],
      transformResponse: (response: DepositMethodInfoResponse, _meta, { payGroupId }) =>
        formDepositMethodInfo(response, formDepositRequiredFields, payGroupId) as DepositMethodInfoResponse,
    }),
    submitDeposit: builder.query<
      PayHubWidgetContractsSubmitPaymentResponse | PayHubWidgetContractsSubmitPayoutResponse,
      DepositSubmitRequestProps
    >({
      query: ({ payGroupId, requestBody, signal }) => ({
        url: `payments/groups/${payGroupId}/submit`,
        method: 'POST',
        body: requestBody,
        signal,
        keepalive: true,
      }),
      providesTags: [SUBMIT_TAG],
      transformErrorResponse: (response: ExtendedFetchBaseQueryError): CustomServerError => ({
        status: response?.status,
        errorMessage: response?.data?.error_message,
        errorCode: response?.data?.error_code,
      }),
    }),
    uploadDocFileForDeposit: builder.mutation<UploadDocFileResponse, UploadDocFileRequest>({
      query: ({ payGroupId, requestBody: formData, signal }) => ({
        url: `payments/groups/${payGroupId}/upload`,
        method: 'POST',
        body: formData,
        signal,
      }),
      transformErrorResponse: (response: ExtendedFetchBaseQueryError): CustomServerError => ({
        status: response?.status,
        errorMessage: response?.data?.error_message,
        errorCode: response?.data?.error_code,
      }),
    }),
  }),
  overrideExisting: false,
});

export const { useGetDepositMethodsQuery, useLazyGetDepositMethodsQuery } = depositAPI;
export const {
  useGetDepositMethodInfoQuery,
  useLazyGetDepositMethodInfoQuery,
  useLazySubmitDepositQuery,
  useUploadDocFileForDepositMutation,
} = extendedDepositAPI;

export const { endpoints } = depositAPI;

export default depositAPI.reducer;
